// LAYOUT-SPEC §0 — Teams (the SOW-first index, D109)
//
//   ONE QUESTION  Which teams does this program run, and which of them owe
//                 something now?
//   PRIMARY OBJECT  A collection of workstreams (Archetype B), plus one row for
//                 the contract-wide deliverables, "Contract-wide (<period>)".
//   PRIMARY VERB    Open a team. New team is the one create.
//   WORST-CASE VOLUME  ~25 teams (OMEGA has 23). One page, no paging.
//
// Each row's numbers are that team's own current window (D104): the in-force
// SOW's interval, else its exercise line's, plus open items carried over.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraEntityCell, SpecraFormField, SpecraInput, SpecraListPage, SpecraPageHeader } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Layers } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { EmptyState } from '../../_shared/components/PageStates';
import { useCmAccess } from '../access';
import { CmDrawer } from '../components/CmDrawer';
import { CmTable } from '../components/common';
import { OccurrenceWindow, useCreateWorkstream, useOccurrences, useOccurrencesMany, useWorkstreams } from '../hooks';
import { todayISO } from '../lib/format';
import { useCmLookups } from '../lookups';
const COUNT_COLUMNS = [
    {
        state: 'late',
        header: 'Late'
    },
    {
        state: 'rejected',
        header: 'Rejected'
    },
    {
        state: 'awaiting_review',
        header: 'Awaiting review'
    },
    {
        state: 'due_soon',
        header: 'Due 14 days'
    }
];
function contractWideCounts(r) {
    if (!r) return undefined;
    const out = {};
    for (const o of r.occurrences){
        if (o.workstreamId || o.retired) continue;
        const s = o.status?.state ?? '';
        out[s] = (out[s] ?? 0) + 1;
    }
    return out;
}
export function useContractWideLabel(programId) {
    const lk = useCmLookups(programId);
    const today = todayISO();
    const current = lk.periods.find((p)=>p.start && p.start <= today && (!p.end || today <= p.end));
    return `Contract-wide${current ? ` (${current.name})` : ''}`;
}
export function TeamsPage() {
    const { programId } = useProgramContext();
    const navigate = useNavigate();
    const access = useCmAccess(programId);
    const teams = useWorkstreams(programId);
    const list = useMemo(()=>teams.data ?? [], [
        teams.data
    ]);
    const windows = useOccurrencesMany(programId, list.map((w)=>({
            workstreamId: w.id,
            window: OccurrenceWindow.WORKSTREAM_CURRENT_WINDOW,
            pageSize: 1
        })));
    const wide = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD
    });
    const wideLabel = useContractWideLabel(programId);
    const [creating, setCreating] = useState(false);
    const rows = useMemo(()=>{
        const teamRows = list.map((w, i)=>({
                id: w.id,
                name: w.name,
                contractWide: false,
                counts: windows[i]?.data?.stateCounts
            })).sort((a, b)=>a.name.localeCompare(b.name));
        const wideCounts = contractWideCounts(wide.data);
        const hasWide = wideCounts && Object.keys(wideCounts).length > 0;
        return hasWide || teamRows.length > 0 ? [
            {
                id: 'contract-wide',
                name: wideLabel,
                contractWide: true,
                counts: wideCounts
            },
            ...teamRows
        ] : [];
    }, [
        list,
        windows,
        wide.data,
        wideLabel
    ]);
    const columns = [
        {
            id: 'name',
            header: 'Team',
            cell: (r)=>/*#__PURE__*/ _jsx("div", {
                    "data-testid": r.contractWide ? Testids.cmTeams.contractWideRow : undefined,
                    children: /*#__PURE__*/ _jsx(SpecraEntityCell, {
                        title: r.name,
                        description: r.contractWide ? 'Deliverables no team owes' : undefined
                    })
                })
        },
        ...COUNT_COLUMNS.map((c)=>({
                id: c.state,
                header: c.header,
                align: 'right',
                cell: (r)=>{
                    const n = r.counts?.[c.state] ?? 0;
                    const alarm = n > 0 && (c.state === 'late' || c.state === 'rejected');
                    return /*#__PURE__*/ _jsx("span", {
                        className: n > 0 ? '' : 'text-muted-foreground',
                        "data-testid": Testids.cmTeams.rowStrip,
                        "data-state": c.state,
                        children: !r.counts ? '…' : alarm ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "danger",
                            children: n
                        }) : n
                    });
                }
            }))
    ];
    const empty = /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.cmTeams.empty,
        children: /*#__PURE__*/ _jsx(EmptyState, {
            icon: Layers,
            title: "No teams yet",
            description: "Teams come from exercise lines; add one by hand to start tracking what it owes.",
            action: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>setCreating(true),
                children: "New team"
            }) : undefined
        })
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraListPage, {
                "data-testid": Testids.cmTeams.page,
                header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    titleSize: "home",
                    title: "Teams",
                    meta: list.length,
                    description: "Each team follows its SOW. Numbers are each team’s current window.",
                    actions: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setCreating(true),
                        "data-testid": Testids.cmTeams.create,
                        children: "New team"
                    }) : undefined
                }),
                children: /*#__PURE__*/ _jsx(CmTable, {
                    caption: "Teams",
                    rows: rows,
                    columns: columns,
                    rowKey: (r)=>r.id,
                    rowTestId: Testids.cmTeams.row,
                    isLoading: teams.isLoading,
                    error: teams.error,
                    onRetry: ()=>void teams.refetch(),
                    onRowClick: (r)=>navigate(`/delivery/programs/${programId}/workstreams/${r.id}`),
                    getRowLabel: (r)=>r.name,
                    empty: empty
                })
            }),
            creating ? /*#__PURE__*/ _jsx(NewTeamDrawer, {
                programId: programId,
                onClose: ()=>setCreating(false),
                onCreated: (id)=>navigate(`/delivery/programs/${programId}/workstreams/${id}`)
            }) : null
        ]
    });
}
function NewTeamDrawer({ programId, onClose, onCreated }) {
    const [name, setName] = useState('');
    const create = useCreateWorkstream(programId, {
        success: 'Team created'
    });
    const save = ()=>create.mutate({
            name: name.trim()
        }, {
            onSuccess: (r)=>r.workstream && onCreated(r.workstream.id)
        });
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "New team",
        description: "A team is one body of work a SOW describes. Link its exercise line next.",
        onClose: onClose,
        busy: create.isPending,
        testId: Testids.cmTeams.createDrawer,
        footerProps: {
            confirmLabel: 'Create team',
            onConfirm: save,
            confirmDisabled: !name.trim() || create.isPending,
            loading: create.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmTeams.createSave
            }
        },
        children: /*#__PURE__*/ _jsx(SpecraFormField, {
            label: "Name",
            required: true,
            hint: "As the SOW or the exercise line names it.",
            children: /*#__PURE__*/ _jsx(SpecraInput, {
                value: name,
                onChange: (e)=>setName(e.target.value),
                maxLength: 300,
                "data-testid": Testids.cmTeams.createName
            })
        })
    });
}
